import {
  Announcement,
  AuditLogEntity,
  BusinessGroupEntity,
  BusinessGroupEntityInput,
  ContactInformation,
  MasterSegment,
  Resources,
  UserMetricsRaw
} from 'src/models/AppContextModels';

import * as mutations from 'src/graphql/mutations';
import * as queries from 'src/graphql/queries';

import { BudgetOwners, BudgetOwnershipTransferHistory, BudgetOwnershipTransferMutation } from 'src/models/BudgetOwnerTransferModel';
import { CapexActualsDetailsMutation } from 'src/models/CapexModels';
import {
  CampaignDimensionMetadata,
  CampaignsMetadata,
  CountryCompanyMapping,
  DmmAccountChannelFunnelMapping,
  DMMProductLineCampaign
} from 'src/models/DmmModel';
import { ForecastInputMutation } from 'src/models/ForecastModels';
import { ForecastPlanningCycleIds, MasterScenarios, PlanningCycleEntity, PlanningCycleSnapshot } from 'src/models/PlanningCycleModel';
import { CreateXVTSnapshotMutation, RefreshFromLiveMutation, RefreshXVTFromLiveMutation, XVTSnapshot } from 'src/API';
import { POActualsDetailsMutation, POFilterDropdowns } from 'src/models/POTaggingModel';
import { XptMetadata } from 'src/models/XptGenericModels';
import { AccountBudgetTypeMapping, ExpenseTypeMappingEntity, ProjectDetailsInput, UsefulLifeMappingEntity } from 'src/models/xPTMappingModels';
import { UserAccessEntity } from 'src/models/XptUsersModel';
import {
  CreateExportXvtForecastDetailsResponse,
  CreateXvtSnapshotMutationInput,
  ExportXvtToHdwMutationInput,
  ExportXvtToHdwResponse,
  UpsertXvtForecastDetailsResponse,
  XvtForecastDetailsMutationInput
} from 'src/models/XvtModels';
import { fetchGraphQLRequest } from './graphql-api';

const isAuthorizationDenied = (error: any): boolean => {
  const message = (error?.message || '').toString().toLowerCase();
  return message.includes('not authorized to perform this action') || message.includes('unauthorized') || message.includes('28000');
};

export const getUserAccessAuthorization = async (): Promise<UserAccessEntity[]> => {
  try {
    const data = await fetchGraphQLRequest<{ listUserAccess: UserAccessEntity[] }>({
      query: queries.listUserAccess,
      variables: {},
      requestName: 'listUserAccess',
      requestType: 'query'
    });
    return data.listUserAccess;
  } catch (error: any) {
    // listUserAccess is the query that TELLS the app whether the caller has any access.
    // If the backend authz gate denies a no-role caller, treat that exactly like the
    // "no access rows" case: return an empty list so downstream authorization resolves
    // to currentUserNotFound=true and the app routes to the request-access page, rather
    // than surfacing a "failed to load" error.
    if (isAuthorizationDenied(error)) {
      return [];
    }
    throw error;
  }
};

export const getXptMetadata = async (): Promise<XptMetadata[]> => {
  try {
    const data = await fetchGraphQLRequest<{ listMetadata: XptMetadata[] }>({
      query: queries.listMetadata,
      variables: {},
      requestName: 'listMetadata',
      requestType: 'query'
    });
    return data.listMetadata;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Retrieves master segments data from the API.
 * @returns A Promise resolving to an array of MasterSegments.
 */
export const getMasterSegments = async (): Promise<MasterSegment[]> => {
  try {
    const data = await fetchGraphQLRequest<{ listMasterSegments: MasterSegment[] }>({
      query: queries.listMasterSegments,
      variables: {},
      requestName: 'listMasterSegments',
      requestType: 'query'
    });
    return data.listMasterSegments;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Retrieves all business groups from the API.
 * @returns A Promise resolving to an array of business groups.
 */
export const getAllBusinessGroups = async (): Promise<BusinessGroupEntity[]> => {
  try {
    const data = await fetchGraphQLRequest<{ listBusinessGroups: BusinessGroupEntity[] }>({
      query: queries.listBusinessGroups,
      variables: {},
      requestName: 'listBusinessGroups',
      requestType: 'query'
    });
    return data.listBusinessGroups;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Retrieves scenarios - listScenarios
 * @returns A Promise resolving to the list of scenarios.
 */
export const getScenarios = async (): Promise<MasterScenarios[]> => {
  try {
    const data = await fetchGraphQLRequest<{ listScenarios: MasterScenarios[] }>({
      query: queries.listScenarios,
      variables: {},
      requestName: 'listScenarios',
      requestType: 'query'
    });
    return data.listScenarios;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Retrieves all Planning Cycles
 */
export const getAllPlanningCycles = async (): Promise<PlanningCycleEntity[]> => {
  try {
    const data = await fetchGraphQLRequest<{ listPlanningCycles: PlanningCycleEntity[] }>({
      query: queries.listPlanningCycles,
      variables: {},
      requestName: 'listPlanningCycles',
      requestType: 'query'
    });
    return data.listPlanningCycles;
  } catch (error: any) {
    throw error;
  }
};

export const getAllPlanningCycleSnapshots = async (): Promise<PlanningCycleSnapshot[]> => {
  try {
    const data = await fetchGraphQLRequest<{ listScenarioSnapshots: PlanningCycleSnapshot[] }>({
      query: queries.listScenarioSnapshots,
      variables: {},
      requestName: 'listScenarioSnapshots',
      requestType: 'query'
    });
    return data.listScenarioSnapshots;
  } catch (error: any) {
    throw error;
  }
};

export const getContactDetails = async (): Promise<ContactInformation[]> => {
  try {
    const data = await fetchGraphQLRequest<{ listContactDetails: ContactInformation[] }>({
      query: queries.listContactDetails,
      variables: {},
      requestName: 'listContactDetails',
      requestType: 'query'
    });
    return data.listContactDetails || [];
  } catch (error: any) {
    throw error;
  }
};

export const getResources = async (): Promise<Resources[]> => {
  try {
    const data = await fetchGraphQLRequest<{ listResourcesDetails: Resources[] }>({
      query: queries.listResourcesDetails,
      variables: {},
      requestName: 'listResourcesDetails',
      requestType: 'query'
    });
    return data.listResourcesDetails || [];
  } catch (error: any) {
    throw error;
  }
};

export const getAnnouncements = async (): Promise<Announcement[]> => {
  try {
    const data = await fetchGraphQLRequest<{ listAnnouncementDetails: any[] }>({
      query: queries.listAnnouncementDetails,
      variables: {},
      requestName: 'listAnnouncementDetails',
      requestType: 'query'
    });
    return data.listAnnouncementDetails;
  } catch (error: any) {
    throw new Error(`Failed to get announcements: ${error.message}`);
  }
};

export const getUserMetrics = async (): Promise<UserMetricsRaw[]> => {
  try {
    const data = await fetchGraphQLRequest<{ listUserMetricsDetails: UserMetricsRaw[] }>({
      query: queries.listUserMetricsDetails,
      variables: {},
      requestName: 'listUserMetricsDetails',
      requestType: 'query'
    });
    return data.listUserMetricsDetails || [];
  } catch (error: any) {
    throw error;
  }
};

export const getUsefulLifeDetails = async (): Promise<UsefulLifeMappingEntity[]> => {
  try {
    const data = await fetchGraphQLRequest<{ listUsefulLifeDetails: UsefulLifeMappingEntity[] }>({
      query: queries.listUsefulLifeDetails,
      variables: {},
      requestName: 'listUsefulLifeDetails',
      requestType: 'query'
    });
    return data.listUsefulLifeDetails || [];
  } catch (error: any) {
    throw error;
  }
};

export const getExpenseTypeMappings = async (): Promise<ExpenseTypeMappingEntity[]> => {
  try {
    const data = await fetchGraphQLRequest<{ listExpenseTypeDetails: ExpenseTypeMappingEntity[] }>({
      query: queries.listExpenseTypeDetails,
      variables: {},
      requestName: 'listExpenseTypeDetails',
      requestType: 'query'
    });
    return data.listExpenseTypeDetails || [];
  } catch (error: any) {
    throw error;
  }
};

export const getAuditLogs = async (): Promise<AuditLogEntity[]> => {
  try {
    const data = await fetchGraphQLRequest<{ listAuditLogDetails: AuditLogEntity[] }>({
      query: queries.listAuditLogDetails,
      variables: {},
      requestName: 'listAuditLogDetails',
      requestType: 'query'
    });
    return data.listAuditLogDetails || [];
  } catch (error: any) {
    throw error;
  }
};

export const getCurrentBudgetOwners = async (): Promise<BudgetOwners[]> => {
  try {
    const data = await fetchGraphQLRequest<{ listBudgetOwners: BudgetOwners[] }>({
      query: queries.listBudgetOwners,
      variables: {},
      requestName: 'listBudgetOwners',
      requestType: 'query'
    });
    return data.listBudgetOwners || [];
  } catch (error: any) {
    throw error;
  }
};

export const getBudgetOwnerTransferHistory = async (): Promise<BudgetOwnershipTransferHistory[]> => {
  try {
    const data = await fetchGraphQLRequest<{ listTransferBudgetOwnershipLog: BudgetOwnershipTransferHistory[] }>({
      query: queries.listTransferBudgetOwnershipLog,
      variables: {},
      requestName: 'listTransferBudgetOwnershipLog',
      requestType: 'query'
    });
    return data.listTransferBudgetOwnershipLog || [];
  } catch (error: any) {
    throw error;
  }
};

// PO Filter Dropdowns
export const getPOTaggingMonthsAndCC = async (dataClassificationId: number): Promise<POFilterDropdowns> => {
  try {
    const data = await fetchGraphQLRequest<{ listActualsFilterData: POFilterDropdowns }>({
      query: queries.listActualsFilterData,
      variables: { data_classification_id: dataClassificationId },
      requestName: 'listActualsFilterData',
      requestType: 'query'
    });
    return data.listActualsFilterData || [];
  } catch (error: any) {
    throw error;
  }
};

// DMM Campaigns Metadata
export const getCampaignMetadata = async (): Promise<CampaignsMetadata[]> => {
  try {
    const data = await fetchGraphQLRequest<{ listDMMCampaignsMetadata: CampaignsMetadata[] }>({
      query: queries.listDMMCampaignsMetadata,
      variables: {},
      requestName: 'listDMMCampaignsMetadata',
      requestType: 'query'
    });
    return data.listDMMCampaignsMetadata || [];
  } catch (error: any) {
    throw error;
  }
};

// DMM Campaign Dimensions
export const getCampaignDimensionsMetadata = async (): Promise<CampaignDimensionMetadata[]> => {
  try {
    const data = await fetchGraphQLRequest<{ listDMMCampaignDimensions: CampaignDimensionMetadata[] }>({
      query: queries.listDMMCampaignDimensions,
      variables: {},
      requestName: 'listDMMCampaignDimensions',
      requestType: 'query'
    });
    return data.listDMMCampaignDimensions || [];
  } catch (error: any) {
    throw error;
  }
};

// DMM Country Company Mapping
export const getCountryCompanyMapping = async (): Promise<CountryCompanyMapping[]> => {
  try {
    const data = await fetchGraphQLRequest<{ listCountryCompanyMapping: CountryCompanyMapping[] }>({
      query: queries.listCountryCompanyMapping,
      variables: {},
      requestName: 'listCountryCompanyMapping',
      requestType: 'query'
    });
    return data.listCountryCompanyMapping || [];
  } catch (error: any) {
    throw error;
  }
};

// One reference table shared by all product lines, so the query takes no arguments.
export const getDmmAccountChannelFunnelMapping = async (): Promise<DmmAccountChannelFunnelMapping[]> => {
  const data = await fetchGraphQLRequest<{ listDmmAccountChannelFunnelMapping: DmmAccountChannelFunnelMapping[] }>({
    query: queries.listDmmAccountChannelFunnelMapping,
    variables: {},
    requestName: 'listDmmAccountChannelFunnelMapping',
    requestType: 'query'
  });
  return data.listDmmAccountChannelFunnelMapping || [];
};

export const getAccountBudgetTypeMappings = async (): Promise<AccountBudgetTypeMapping[]> => {
  try {
    const data = await fetchGraphQLRequest<{ listBudgetTypeDetails: AccountBudgetTypeMapping[] }>({
      query: queries.listBudgetTypeDetails,
      variables: {},
      requestName: 'listBudgetTypeDetails',
      requestType: 'query'
    });
    return data.listBudgetTypeDetails || [];
  } catch (error: any) {
    throw error;
  }
};

export const getForecastingPlanningCycles = async (): Promise<ForecastPlanningCycleIds[]> => {
  try {
    const data = await fetchGraphQLRequest<{ listForecastPlanningCycles: ForecastPlanningCycleIds[] }>({
      query: queries.listForecastPlanningCycles,
      variables: {},
      requestName: 'listForecastPlanningCycles',
      requestType: 'query'
    });
    return data.listForecastPlanningCycles || [];
  } catch (error: any) {
    throw error;
  }
};

// Mutations

export const updateCampaigns = async (mutationData: DMMProductLineCampaign[]): Promise<any> => {
  return fetchGraphQLRequest<any>({
    query: mutations.createOrUpdateDMMProductLineCampaigns,
    variables: { input: mutationData },
    requestName: 'createOrUpdateDMMProductLineCampaigns',
    requestType: 'mutation'
  });
};

export const createOrUpdateUserAccessMutation = async (mutationData: UserAccessEntity | UserAccessEntity[]): Promise<any> => {
  return fetchGraphQLRequest<any>({
    query: mutations.createOrUpdateUserAccess,
    variables: { input: mutationData },
    requestName: 'createOrUpdateUserAccess',
    requestType: 'mutation'
  });
};

/**
 * Planning Cycle Form Submission
 */
export const planningCycleFormMutation = async (planningCycleForm: PlanningCycleEntity[]): Promise<any> => {
  // Strip frontend-only fields that aren't in PlanningCycleInput
  const input = planningCycleForm.map(({ scenario_year, extended_users, ...rest }) => rest);
  return fetchGraphQLRequest<any>({
    query: mutations.createPlanningCycles,
    variables: { input },
    requestName: 'createPlanningCycle',
    requestType: 'mutation'
  });
};

/**
 * Refresh an OP planning cycle's forecast data from the active R&O (live) scenario.
 */
export const refreshFromLiveMutation = async (
  scenario_seq_id: number,
  updated_by: string,
  export_s3_bucket: string
): Promise<RefreshFromLiveMutation> => {
  return fetchGraphQLRequest<RefreshFromLiveMutation>({
    query: mutations.refreshFromLive,
    variables: { input: { scenario_seq_id, updated_by, export_s3_bucket } },
    requestName: 'refreshFromLive',
    requestType: 'mutation'
  });
};

/**
 * Refresh an xVT OP planning cycle from the active R&O (live) scenario. The resolver injects
 * `updated_by` (caller alias) and the S3 bucket/region server-side, so the input is just the
 * target `scenario_seq_id` - unlike forecast's 3-field `refreshFromLiveMutation`.
 */
export const refreshXVTFromLive = async (scenario_seq_id: number): Promise<RefreshXVTFromLiveMutation> => {
  return fetchGraphQLRequest<RefreshXVTFromLiveMutation>({
    query: mutations.refreshXVTFromLive,
    variables: { input: { scenario_seq_id } },
    requestName: 'refreshXVTFromLive',
    requestType: 'mutation'
  });
};

/**
 * Submit Snapshot Create Mutation
 */
export const createScenarioSnapshotFormMutation = async (scenarioSnapshotFormValues: PlanningCycleSnapshot): Promise<any> => {
  return fetchGraphQLRequest<any>({
    query: mutations.createScenarioSnapshot,
    variables: { scenarioSnapshotJson: scenarioSnapshotFormValues },
    requestName: 'createScenarioSnapshot',
    requestType: 'mutation'
  });
};

/**
 * Submit Business Group changes from Onboarding module
 */
export const upsertBusinessGroupMutation = async (onboardingSubmitModel: BusinessGroupEntityInput): Promise<any> => {
  return fetchGraphQLRequest<any>({
    query: mutations.createBusinessGroup,
    variables: { dataClassificationMDJson: onboardingSubmitModel },
    requestName: 'createBusinessGroup',
    requestType: 'mutation'
  });
};

/**
 * Update Forecast Template Data
 */
export const updateForecastDetails = async (mutationData: ForecastInputMutation): Promise<any> => {
  return fetchGraphQLRequest<any>({
    query: mutations.updateForecastDetails,
    variables: { forecastDetailsJson: mutationData },
    requestName: 'updateForecastDetails',
    requestType: 'mutation'
  });
};

/**
 * Create Export Forecast Template Data
 */
export const createExportForecastDetails = async (mutationData: ForecastInputMutation): Promise<any> => {
  return fetchGraphQLRequest<any>({
    query: mutations.createExportForecastDetails,
    variables: { input: mutationData },
    requestName: 'createExportForecastDetails',
    requestType: 'mutation'
  });
};

/**
 * Upsert xVT Forecast Input Data.
 *
 * xVT mirrors forecast's two-phase write: the FE first uploads the rows JSON to S3, then calls this
 * mutation with a pointer (`s3_update_file_key`) - never the rows themselves. The AppSync resolver
 * injects `s3_bucket_name` / `s3_region` / `updated_by` server-side, so the input is the reduced
 * 5-field `XvtForecastDetailsMutationInput` (NOT forecast's 8-field `ForecastInputMutation`).
 */
export const upsertXVTForecastDetails = async (mutationData: XvtForecastDetailsMutationInput): Promise<UpsertXvtForecastDetailsResponse> => {
  return fetchGraphQLRequest<UpsertXvtForecastDetailsResponse>({
    query: mutations.upsertXVTForecastDetails,
    variables: { input: mutationData },
    requestName: 'upsertXVTForecastDetails',
    requestType: 'mutation'
  });
};

/**
 * Create Export xVT Forecast Data - re-stages the readable S3 snapshot
 * (`query/list_xvt_forecast_detail.json`) the FE reads back. Same input as the upsert.
 */
export const createExportXVTForecastDetails = async (
  mutationData: XvtForecastDetailsMutationInput
): Promise<CreateExportXvtForecastDetailsResponse> => {
  return fetchGraphQLRequest<CreateExportXvtForecastDetailsResponse>({
    query: mutations.createExportXVTForecastDetails,
    variables: { input: mutationData },
    requestName: 'createExportXVTForecastDetails',
    requestType: 'mutation'
  });
};

/**
 * Export xVT rows to OPD Hardware (HDW). Pure DB->HDW push (no S3). Input drops `s3_update_file_key`.
 */
export const exportXVTToHDW = async (mutationData: ExportXvtToHdwMutationInput): Promise<ExportXvtToHdwResponse> => {
  return fetchGraphQLRequest<ExportXvtToHdwResponse>({
    query: mutations.exportXVTToHDW,
    variables: { input: mutationData },
    requestName: 'exportXVTToHDW',
    requestType: 'mutation'
  });
};

/**
 * Create a point-in-time xVT snapshot for a scenario (PRD 4.5). The resolver injects
 * `s3_bucket_name` / `s3_region` / `created_by` / `updated_by` server-side, so the input is the
 * reduced 4-field `CreateXvtSnapshotMutationInput` (s3_key, scenario_seq_id, snapshot_name, note).
 */
export const createXVTSnapshot = async (mutationData: CreateXvtSnapshotMutationInput): Promise<CreateXVTSnapshotMutation> => {
  return fetchGraphQLRequest<CreateXVTSnapshotMutation>({
    query: mutations.createXVTSnapshot,
    variables: { input: mutationData },
    requestName: 'createXVTSnapshot',
    requestType: 'mutation'
  });
};

/** Lists all xVT snapshots the caller can see (resolver scopes by role/business group). */
export const listXVTSnapshots = async (): Promise<XVTSnapshot[]> => {
  const data = await fetchGraphQLRequest<{ listXVTSnapshots: XVTSnapshot[] | null }>({
    query: queries.listXVTSnapshots,
    variables: {},
    requestName: 'listXVTSnapshots',
    requestType: 'query'
  });
  return data.listXVTSnapshots ?? [];
};

/**
 * Update Home Page - Resources
 */
export const updateResourcesData = async (mutationData: Resources[]): Promise<any> => {
  return fetchGraphQLRequest<any>({
    query: mutations.createOrUpdateResourcesDetails,
    variables: { input: mutationData },
    requestName: 'createOrUpdateResourcesDetails',
    requestType: 'mutation'
  });
};

export const updatePOTaggingData = async (mutationData: POActualsDetailsMutation): Promise<any> => {
  return fetchGraphQLRequest<any>({
    query: mutations.updatePOActualsDetails,
    variables: { input: mutationData },
    requestName: 'updatePOActualsDetails',
    requestType: 'mutation'
  });
};

export const updateUsefulLifeMappings = async (mutationData: UsefulLifeMappingEntity[]): Promise<any> => {
  return fetchGraphQLRequest<any>({
    query: mutations.createOrUpdateUsefulLifeDetails,
    variables: { input: mutationData },
    requestName: 'createOrUpdateUsefulLifeDetails',
    requestType: 'mutation'
  });
};

export const updateCapex = async (mutationData: CapexActualsDetailsMutation): Promise<any> => {
  return fetchGraphQLRequest<any>({
    query: mutations.createOrUpdateCapexActualsDetails,
    variables: { input: mutationData },
    requestName: 'createOrUpdateCapexActualsDetails',
    requestType: 'mutation'
  });
};

export const updateExpenseType = async (mutationData: ExpenseTypeMappingEntity[]): Promise<any> => {
  return fetchGraphQLRequest<any>({
    query: mutations.createOrUpdateExpenseTypeDetails,
    variables: { input: mutationData },
    requestName: 'createOrUpdateExpenseTypeDetails',
    requestType: 'mutation'
  });
};

export const updateBudgetOwnerTransfer = async (mutationData: BudgetOwnershipTransferMutation): Promise<any> => {
  return fetchGraphQLRequest<any>({
    query: mutations.updateBudgetOwnership,
    variables: { input: mutationData },
    requestName: 'updateBudgetOwnership',
    requestType: 'mutation'
  });
};

export const updateAnnouncement = async (mutationData: Announcement[]): Promise<any> => {
  return fetchGraphQLRequest<any>({
    query: mutations.createOrUpdateAnnouncementDetails,
    variables: { input: mutationData },
    requestName: 'createOrUpdateAnnouncementDetails',
    requestType: 'mutation'
  });
};

export const updateContacts = async (mutationData: ContactInformation[]): Promise<any> => {
  return fetchGraphQLRequest<any>({
    query: mutations.createOrUpdateContactDetails,
    variables: { input: mutationData },
    requestName: 'createOrUpdateContactDetails',
    requestType: 'mutation'
  });
};

export const updateProjectMapping = async (mutationData: ProjectDetailsInput): Promise<any> => {
  return fetchGraphQLRequest<any>({
    query: mutations.updateAndExportProjectDetails,
    variables: { input: mutationData },
    requestName: 'updateAndExportProjectDetails',
    requestType: 'mutation'
  });
};

// ─── DMM Invoice-Level Spend Mappings ─────────────────────────────────────────

import {
  DmmInvoiceMappingReference,
  DmmInvoiceMappingsInput,
  DmmInvoiceMappingsListResponse,
  DmmInvoiceMappingsMutationResponse
} from 'src/models/DmmInvoiceMappingModel';

const EMPTY_INVOICE_MAPPING_REFERENCE: DmmInvoiceMappingReference = {
  channels: [],
  funnels: [],
  company_country: [],
  ringblink_account_channel: [],
  ringblink_location_country: [],
  ringblink_product_codes: [],
  product_lines: []
};

/**
 * Defensively parse an AWSJSON field. It arrives as a string; a malformed payload
 * degrades to null (callers fall back to defaults) instead of throwing and rejecting
 * the promise. Mirrors the AWSJSON parsing pattern used elsewhere in the codebase.
 */
const parseAwsJson = <T>(raw: unknown): Partial<T> | null => {
  if (typeof raw !== 'string') return (raw as Partial<T> | null) ?? null;
  try {
    return JSON.parse(raw) as Partial<T>;
  } catch {
    return null;
  }
};

/**
 * Fetches DMM invoice-level mappings (current + inherited) and reference data for
 * a business group and month. Response is AWSJSON — arrives as a string; parsed
 * defensively.
 */
export const getDmmInvoiceMappings = async (dataClassificationId: number, periodName: string): Promise<DmmInvoiceMappingsListResponse> => {
  const data = await fetchGraphQLRequest<{ listDmmInvoiceMappings: string }>({
    query: queries.listDmmInvoiceMappings,
    variables: { data_classification_id: dataClassificationId, period_name: periodName },
    requestName: 'listDmmInvoiceMappings',
    requestType: 'query'
  });
  const raw = data.listDmmInvoiceMappings;
  const parsed = parseAwsJson<DmmInvoiceMappingsListResponse>(raw);
  return {
    current: parsed?.current ?? [],
    inherited: parsed?.inherited ?? [],
    reference: { ...EMPTY_INVOICE_MAPPING_REFERENCE, ...(parsed?.reference ?? {}) }
  };
};

/**
 * Saves DMM invoice-level mappings for one month. Response is AWSJSON — parsed
 * defensively into the per-row summary.
 */
export const createOrUpdateDmmInvoiceMappings = async (input: DmmInvoiceMappingsInput): Promise<DmmInvoiceMappingsMutationResponse> => {
  const data = await fetchGraphQLRequest<{ createOrUpdateDmmInvoiceMappings: string }>({
    query: mutations.createOrUpdateDmmInvoiceMappings,
    variables: { input },
    requestName: 'createOrUpdateDmmInvoiceMappings',
    requestType: 'mutation'
  });
  const raw = data.createOrUpdateDmmInvoiceMappings;
  const parsed = parseAwsJson<DmmInvoiceMappingsMutationResponse>(raw);
  return {
    updated: parsed?.updated ?? 0,
    unchanged: parsed?.unchanged ?? 0,
    failed: parsed?.failed ?? 0,
    errors: parsed?.errors ?? []
  };
};

/**
 * Trigger an on-demand refresh of the DMM breakdown report's Actuals S3 partition.
 * Runs an in-DB aws_s3 export (via a PostgreSQL function) so PO/invoice Channel/Funnel
 * mapping edits reflect on the report's Page 0 within seconds, without the full
 * allocation reload. The resolver is authz-gated on the data classification.
 */
export const refreshDmmBreakdownExport = async (dataClassificationId: number): Promise<void> => {
  await fetchGraphQLRequest<{ refreshDmmBreakdownExport: { numberOfRecordsUpdated: number } }>({
    query: mutations.refreshDmmBreakdownExport,
    variables: { input: { data_classification_id: dataClassificationId } },
    requestName: 'refreshDmmBreakdownExport',
    requestType: 'mutation'
  });
};

/**
 * Trigger an on-demand refresh of the DMM detail (line-level) report's Actuals S3 partition.
 * Runs an in-DB aws_s3 export so channel/funnel/country mapping edits reflect on the Deep Dive
 * detail report within seconds, without the full allocation reload. Authz-gated on the data
 * classification.
 */
export const refreshDmmDetailExport = async (dataClassificationId: number): Promise<void> => {
  await fetchGraphQLRequest<{ refreshDmmDetailExport: { numberOfRecordsUpdated: number } }>({
    query: mutations.refreshDmmDetailExport,
    variables: { input: { data_classification_id: dataClassificationId } },
    requestName: 'refreshDmmDetailExport',
    requestType: 'mutation'
  });
};

/** Re-export a single dmm_invoiced partition (period x product) so the mapping grid reflects a save. */
export const refreshDmmInvoicedPartition = async (
  dataClassificationId: number,
  periodName: string,
  productCode: string
): Promise<void> => {
  await fetchGraphQLRequest<{ refreshDmmInvoicedPartition: { numberOfRecordsUpdated: number } }>({
    query: mutations.refreshDmmInvoicedPartition,
    variables: { input: { data_classification_id: dataClassificationId, period_name: periodName, product_code: productCode } },
    requestName: 'refreshDmmInvoicedPartition',
    requestType: 'mutation'
  });
};
